import React, {useCallback, useState, useEffect} from 'react';
import {Col, Container, Row, Button, Alert} from 'reactstrap';
import {Link} from 'react-scroll';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendar, faCalendarPlus, faCloudArrowDown} from '@fortawesome/pro-regular-svg-icons';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Video from 'components/Video';
import VideoButtons from 'components/Broadcasts/VideoButtons';
import ClosedCaptionModal from 'components/Broadcasts/ClosedCaptionModal';
import ImageCardCL from 'components/ComponentLibrary/ImageCardCL';
import {ListIcon} from 'components/Elements/IconList';
import CountdownTimer from 'components/Elements/CountdownTimer';
import CheckoutButton from 'components/CheckoutButton';
import CommentSection, {CommentSectionInline} from 'components/CommentSection';
import Modal from 'components/Modal';
import Footer from 'components/Elements/Footer';

// @ts-ignore
import {days} from 'data/pbcc-workshop.json';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';

export default function BroadcastIndex({
  noExitIntent = false,
  noFixedVideo = false,
  ctaOverride = null,
  videoUrlOverride = null,
  ctaNoTimer = false,
  variant = 'default',
  version = 'broadcast',
  ...props
}) {
  const pageTitle = 'Free Workshop: Become a Plant-Based Coach';
  const pageDescription = 'Save your spot in this free, online workshop today.';
  const ogImage =
    'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/pbcc/og/pbcc-w-2026-open-graph-workshop-fb-tw.png';
  const shareUrl = 'https://certificate.foodrevolution.org/workshop/';

  const {
    phases: {
      default: defaultSchedule = {phase: ''},
      workshop: workshopSchedule = {phase: 'before'},
      broadcast: broadcastSchedule = {phase: 'before', day: 0, message: {color: '', text: ''}},
      replay: replaySchedule = {phase: 'before', day: 0}
    }
  } = usePhaseState();

  const mainPhase = defaultSchedule.phase;
  const workshopPhase = workshopSchedule.phase;
  const broadcastPhase = broadcastSchedule.phase;
  const replayPhase = replaySchedule.phase;

  const replayScheduleDay = replaySchedule.day;
  const replayUrl = replayPhase.replayUrl ? replayPhase.replayUrl : days[replayScheduleDay].replayVideo;

  const beforeWorkshop =
    workshopPhase === 'before' ||
    (workshopPhase === 'during' && broadcastPhase === 'before' && broadcastSchedule.day === 0);
  const afterWorkshop = workshopPhase === 'after';
  const workshopReplayAll = workshopSchedule.workshopReplayAll;
  const clarityCall = defaultSchedule.clarityCall;

  const workshopDate = new Date('2025-11-18T09:00:00-08:00').getTime();
  const workbookDownloadUrl =
    'https://cdn.foodrevolution.org/members/pdf/pbcc/Plant-Based%20Coaching%20Workshop%202%20-%20Workbook.pdf';
  const commentsUrl = 'https://certificate.foodrevolution.org/workshop/broadcasts/#pbccw202511';

  broadcastSchedule.day =
    broadcastSchedule.day && !isNaN(broadcastSchedule.day)
      ? Math.min(8, Math.max(0, parseInt(broadcastSchedule.day)))
      : 0;

  const productSku = 'pbcc';
  const productSlug = 'pbcc-2026-deposit';
  const productValue = 100;

  const TimerTomorrow = ({day = broadcastSchedule.day}) => {
    if (day + 1 >= days.length) return null;
    const {time: timeLater} = days[day + 1];
    const dateTomorrow = new Date(`${timeLater}`).getTime();
    return (
      <div className="next-broadcast mt-5">
        <h5 className="section-heading mb-2">The next workshop session is in:</h5>
        <CountdownTimer className="countdown-dark mb-3" time={dateTomorrow} />
      </div>
    );
  };

  const [zoomInstructionsIsOpen, zoomInstructionsSetIsOpen] = useState(false);
  const zoomInstructionsOnClick = useCallback(() => zoomInstructionsSetIsOpen(true), []);
  const zoomInstructionsToggle = useCallback(
    () => zoomInstructionsSetIsOpen((zoomInstructionsIsOpen) => !zoomInstructionsIsOpen),
    []
  );

  function WorkshopDownloadButtons({className = undefined}) {
    return (
      <div className={classNames(`workshop-downloads`, className)}>
        <p className="text-center mt-4 mb-2">
          <b>Capture every takeaway with this fillable Workshop Workbook:</b>
        </p>
        <p className="text-center mb-2">
          <Button href={workbookDownloadUrl} target="_blank" color="purple" size="sm" block>
            <FontAwesomeIcon icon={faCloudArrowDown} /> Download Workbook Here
          </Button>
        </p>
      </div>
    );
  }

  function HowItWorksCopy({className = undefined}) {
    return (
      <div className={className}>
        <p className="balance-text">
          <b>How It Works:</b> Each day of the workshop, we will broadcast a new session starting at{' '}
          <b>12 pm ET / 9 am PT / 4 pm GMT</b>. Each session will run for about three hours.
        </p>
        <p>
          After each day’s screening ends, the workshop will be available on{' '}
          <a href="/workshop/replays" target="_blank">
            replay
          </a>{' '}
          for a day.
        </p>
        <p className="balance-text">
          Join Food Revolution Network’s Ocean Robbins, Tracy Garrigan, CHHC, and special guest experts for a live,
          three-day workshop where you’ll learn about plant-based nutrition, coaching, and how to share this information
          — so you can help people heal their lives, and turn your passion for plant-based eating into a deeply
          rewarding vocation.
        </p>
        <div className="calendarBox background-white box-shadow-black-50 rounded mt-4">
          <div className="background-black text-center text-white p-3 rounded-top">
            <h6 className="text-center m-0">Add These Events to Your Calendar</h6>
          </div>
          <div className="p-4">
            <Row className="text-center align-items-center">
              {days.map((day, i) => {
                return (
                  <Col key={`calendar-` + i} lg="4" className="mb-4 mb-lg-0">
                    <Button
                      title="Add to Calendar"
                      className="addeventatc"
                      data-id={day.addEventId}
                      href={`https://www.addevent.com/event/${day.addEventId}`}
                      target="_blank"
                      color="cta"
                    >
                      <p className="text-small">Add to Calendar:</p>
                      <p className="mb-0">
                        <FontAwesomeIcon icon={faCalendarPlus} /> {day.dayOfWeek}, {day.date}
                      </p>
                    </Button>
                  </Col>
                );
              })}
            </Row>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page d-flex flex-column page-nosocial">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />
      <div className="page-content">
        <SectionCL id="" color="purple" innerClass="text-center text-white py-4" angle="none">
          <Container>
            <Row>
              <Col>
                <h1 className="section-heading text-h2 mb-2">
                  {
                    {
                      broadcast: <>Welcome to Food Revolution Network’s</>,
                      replay: <>Food Revolution Network’s</>
                    }[version]
                  }
                </h1>
                <img
                  src="https://cdn.foodrevolution.org/pbcc/pbcc-logo-workshop-white.svg"
                  alt="plant-based coaching workshop"
                  width={600}
                  height={138}
                  style={{maxHeight: '100px', width: 'auto'}}
                />
                <h2 className="text-h3 mt-3 mb-3">
                  <i>Nutrition, Healing, and Coaching In the Modern World</i>
                </h2>
                {version != 'replay' && (
                  <h4 className="mt-0 mb-0">
                    {workshopReplayAll ? <>All Sessions are on Replay Below</> : <>LIVE November 18–20, 2025</>}
                  </h4>
                )}
                {beforeWorkshop && version != 'replay' ? (
                  <CountdownTimer className="countdown-light mt-3" time={workshopDate} />
                ) : null}
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="broadcast-video" color="white" innerClass="pt-0 pt-sm-4 pt-xl-5">
          {afterWorkshop || (workshopPhase === 'during' && workshopReplayAll) ? (
            <Container>
              <Row>
                <Col>
                  {workshopReplayAll && (
                    <>
                      {days.map((day, i) => {
                        return (
                          <div key={'workshop-day-' + day.number} className={i === '2' ? 'mb-0' : 'mb-5'}>
                            <h3 className="section-heading text-center mb-3">
                              Day {day.number}: {day.name}
                            </h3>
                            <p className="balance-text text-center mb-4">{day.description}</p>
                            <Video url={day.replayVideo} label="PBCC-W - Broadcasts - Video" />
                            <div
                              className={classNames(
                                `video-buttons`,
                                `text-center`,
                                `d-flex`,
                                `align-items-center`,
                                `justify-content-center`,
                                `mt-2`
                              )}
                            >
                              <Link
                                className="btn btn-medium-gray text-16 text-800 text-white px-4 py-2 mr-2"
                                activeClass="active"
                                spy={true}
                                offset={0}
                                to="comments"
                                smooth={true}
                                href="#"
                              >
                                Comment Now
                              </Link>
                              <ClosedCaptionModal />
                            </div>
                          </div>
                        );
                      })}
                      <div className="px-4 px-lg-5">
                        <WorkshopDownloadButtons />
                        <p className="text-center balance-text mt-5">
                          The Plant-Based Coaching Workshop has concluded, but we’re excited to announce that we are
                          enrolling for the Plant-Based Coaching Certificate through January 21, 2026, or until we have
                          reached capacity. To learn more about the program and apply, visit{' '}
                          <a href="/join/">https://certificate.foodrevolution.org/join/</a>.
                        </p>
                      </div>
                    </>
                  )}
                </Col>
              </Row>
            </Container>
          ) : (
            <>
              {
                {
                  broadcast: (
                    <>
                      <Container fluid>
                        {broadcastPhase === 'after' ? (
                          <div className="text-center">
                            <h3 className="section-heading">Click this button to access the replay:</h3>
                            <Button color="cta" size="lg" href="/workshop/replays/">
                              Access the Replay
                            </Button>
                            <TimerTomorrow />
                          </div>
                        ) : (
                          <Row className="px-xl-5">
                            <Col xs="12" lg="8" className="px-0 px-sm-3">
                              {broadcastSchedule.message && broadcastSchedule.message.text ? (
                                <Alert
                                  color={broadcastSchedule.message.color ? broadcastSchedule.message.color : 'primary'}
                                >
                                  {broadcastSchedule.message.text}
                                </Alert>
                              ) : null}
                              <Video
                                url={
                                  broadcastSchedule.youtubeUrl
                                    ? broadcastSchedule.youtubeUrl
                                    : 'https://www.youtube.com/watch?v=KFtiT4pDAPU'
                                }
                                label="PBCC-W - Broadcasts - Video"
                                playing
                              />
                              <VideoButtons className="mt-2" />
                              <div className="px-4 px-lg-5">
                                <WorkshopDownloadButtons />
                                <HowItWorksCopy className="mt-4" />
                                <h4>How to Engage and Ask Questions</h4>
                                <div className="text-left">
                                  <p>
                                    It is not required to ask questions during the workshop. If you’d rather just sit
                                    back and listen, you can do so on this page. Or, there are two ways you can ask your
                                    questions for our expert speakers to answer during the workshop:
                                  </p>
                                  <IconListCL number iconAlignment="start">
                                    <>
                                      <p className="mb-1">
                                        Post your questions, thoughts, comments, and reactions in the comment stream on
                                        this page below or in the right sidebar.
                                      </p>
                                      <p className="mb-0">
                                        <b>Note:</b> You’ll need a Facebook account to post questions on this page.
                                      </p>
                                    </>
                                    <>
                                      <p>
                                        Click through the Zoom link. This option will open up the Zoom Webinar, where
                                        you’ll join as an audience member and can ask questions directly in the Q&A box.
                                        If you’re new to Zoom, you can either{' '}
                                        <a href="https://zoom.us/download" target="_blank">
                                          download the app for free here
                                        </a>
                                        , or you{' '}
                                        <a className="link-purple" role="button" onClick={zoomInstructionsOnClick}>
                                          can join from your browser
                                        </a>
                                        .
                                      </p>
                                      <p className="mb-2">
                                        <b>The Zoom links are below:</b>
                                      </p>
                                      <Row className="align-items-center">
                                        {days.map((day, i) => {
                                          return (
                                            <Col key={`zoomLink-` + i} xs="12" xl="4" className="mb-3">
                                              <Button color="purple" href={day.zoomLink} target="_blank" block>
                                                <p className="text-16">Click here if you would like to join:</p>
                                                <p className="mb-0">
                                                  Day {i + 1}, {day.date}
                                                </p>
                                              </Button>
                                            </Col>
                                          );
                                        })}
                                      </Row>
                                    </>
                                  </IconListCL>
                                </div>
                              </div>
                            </Col>
                            <Col xs="12" lg="4" className="d-none d-lg-flex">
                              <div className="box-shadow-black-25 rounded p-3 w-100">
                                <CommentSectionInline
                                  id="comments"
                                  numPosts="10"
                                  url={commentsUrl}
                                  isBroadcastPage
                                  shareUrl={shareUrl}
                                  shareImg={ogImage}
                                />
                              </div>
                            </Col>
                          </Row>
                        )}
                      </Container>
                    </>
                  ),
                  replay: (
                    <>
                      <Container className="pt-4 pt-sm-0">
                        {workshopPhase === 'during' && replayPhase != 'after' && broadcastPhase === 'during' ? (
                          <div className="text-center mb-4">
                            <h4 className="section-heading">
                              Click below to access the Workshop session going on now:
                            </h4>
                            <Button color="cta" size="lg" href="/workshop/broadcasts/">
                              Watch Now!
                            </Button>
                          </div>
                        ) : (workshopPhase === 'during' && replayPhase === 'after' && !replayUrl) ||
                          (workshopPhase === 'during' && broadcastPhase === 'after' && replayPhase != 'after') ? (
                          <div className="background-light-gray box-shadow-black-50 text-center rounded p-4 p-lg-5 mb-4">
                            <h5 className="m-0">Today’s replay will be available soon!</h5>
                          </div>
                        ) : workshopPhase === 'during' && replayPhase === 'after' && replayUrl ? (
                          <>
                            <h3 className="section-heading text-center mb-2">
                              Replay for Day {days[replayScheduleDay].number}
                            </h3>
                            <h4 className="section-heading text-center">{days[replayScheduleDay].name}</h4>
                            <Video url={replayUrl} />
                            <VideoButtons className="mt-2 mb-4" replay />
                          </>
                        ) : (
                          <div className="text-center mb-4">
                            <h4 className="section-heading">Join LIVE November 18–20, 2025</h4>
                            <Button color="cta" size="lg" href="/workshop/broadcasts/">
                              Join Us!
                            </Button>
                          </div>
                        )}
                        <WorkshopDownloadButtons />
                        <HowItWorksCopy className="mt-4" />
                      </Container>
                    </>
                  )
                }[version]
              }
            </>
          )}
        </SectionCL>

        {!afterWorkshop || workshopReplayAll ? (
          <SectionCL id="" color="light-gray">
            <Container>
              <Row>
                <Col>
                  <h4 className="section-heading text-center mb-5">
                    Bring the Food Revolution to the World as a Plant-Based Coach
                  </h4>
                  <ImageCardCL
                    image="https://cdn.foodrevolution.org/pbcc/images/1838520701-6x5.jpg"
                    imageAlt="woman sits across from unrecognizable woman in chairs next to window"
                    className="cardDay1"
                  >
                    <h6 className="section-heading text-center mb-2">Day 1</h6>
                    <h6 className="text-center mt-0 mb-3">Purpose-Driven Coaching: Real Stories, Real Impact</h6>
                    <ul className="text-16">
                      <li>
                        <b>Discover and Define Your Purpose:</b> Gain powerful tools to clarify and articulate your
                        “why,” so you can align your coaching journey with your deepest values.
                      </li>
                      <li>
                        <b>Learn Advanced Nutritional Strategies for Longevity and Vitality:</b> Explore evidence-based
                        nutrition insights with a renowned dietitian to deepen your knowledge and expand your ability to
                        help others thrive.
                      </li>
                      <li>
                        <b>Unlock the Little-Known Art of Transformative Coaching:</b> Dive into six proven techniques
                        for guiding behavior change that lasts, so you can help people move from inspiration to real
                        results.
                      </li>
                      <li>
                        <b>Engage with Experts and Alumni:</b> Ask questions, connect with our speakers, and hear
                        stories from graduates who are already making an impact in the world.
                      </li>
                      <li>
                        <b>Integrate and Clarify Your Next Steps:</b> Walk away with renewed clarity, practical tools
                        for change, and a deeper connection to the “why” that fuels your coaching journey.
                      </li>
                    </ul>
                  </ImageCardCL>
                  <ImageCardCL
                    image="https://cdn.foodrevolution.org/pbcc/images/1740809156-6x5.jpg"
                    imageAlt="waist-up view of senior man holding kitchen knife and carrot, mature woman holding smart phone, both in aprons and laughing."
                    className="cardDay2"
                  >
                    <h6 className="section-heading text-center mb-2">Day 2</h6>
                    <h6 className="text-center mt-0 mb-3">The Inner Work: Nutrition and Self-Care in Coaching</h6>
                    <ul className="text-16">
                      <li>
                        <b>Strengthen Your Mindset for Impact:</b> Discover the tools and practices that help you turn
                        inspiration into sustainable action — for yourself and the people you’ll coach.
                      </li>
                      <li>
                        <b>Cut Through Confusion with Clarity:</b> Learn how to debunk common nutrition myths with
                        authority, so you can build trust and confidence with future clients.
                      </li>
                      <li>
                        <b>Communicate with Power and Heart:</b> Develop the knack for making plant-based messages
                        clear, actionable, and inspiring, whether you’re speaking to individuals, groups, or entire
                        communities.
                      </li>
                      <li>
                        <b>Learn from Real-World Examples:</b> Hear from alumni who are already putting these skills
                        into practice, and pick up practical tips and insights from their journeys.
                      </li>
                      <li>
                        <b>Bridge Knowledge into Transformation:</b> Leave Day 2 with a deeper connection to the inner
                        work of coaching, and the ability to translate nutrition knowledge into true, lasting change for
                        both you and those you serve.
                      </li>
                    </ul>
                  </ImageCardCL>
                  <ImageCardCL
                    image="https://cdn.foodrevolution.org/pbcc/images/2220160484-6x5.jpg"
                    imageAlt="portrait of a businesswoman holding a clipboard in a modern office with a green world map in the background"
                    className="cardDay3 mb-5"
                  >
                    <h6 className="section-heading text-center mb-2">Day 3</h6>
                    <h6 className="text-center mt-0 mb-3">
                      Success Your Way: Growing Your Coaching Practice with Integrity in a Changing World
                    </h6>
                    <ul className="text-16">
                      <li>
                        <b>Build with Purpose and Integrity:</b> Discover how to grow a coaching practice that’s
                        grounded in your values, aligned with your vision, and resilient in a rapidly changing world.
                      </li>
                      <li>
                        <b>Harness AI Wisely:</b> Learn how to use AI ethically and effectively — as a supportive tool
                        that enhances your work while keeping the heart of coaching human-centered and
                        relationship-driven.
                      </li>
                      <li>
                        <b>Amplify Impact Through Groups:</b> Explore how group coaching can expand your influence,
                        increase access, and create powerful peer-to-peer accountability and transformation.
                      </li>
                      <li>
                        <b>Celebrate Diverse Paths to Success:</b> Hear directly from our alumni in a dynamic panel
                        discussion, as they share how success in coaching isn’t about fitting a mold, it’s about
                        aligning your work with your values and making a difference where it matters most to you.
                      </li>
                      <li>
                        <b>Design Aligned Coaching Packages:</b> Gain practical guidance on creating coaching programs
                        and packages that reflect your values and allow you to serve with clarity, compassion, and
                        grace.
                      </li>
                      <li>
                        <b>Step Forward with Confidence:</b> Leave Day 3 with renewed clarity, practical resources, and
                        the confidence to launch or grow a coaching practice that is uniquely yours, and that makes a
                        meaningful difference.
                      </li>
                    </ul>
                  </ImageCardCL>
                  <p>
                    We’ll also introduce Food Revolution Network’s Plant-Based Coaching Certificate, where you can gain
                    the skills to:
                  </p>
                  <ListIcon>
                    <>
                      Launch a successful business as a Plant-Based Coach, or add plant-based coaching to your existing
                      practice.
                    </>
                    <>Get hired as a part-time or full-time employee by forward-thinking companies and organizations.</>
                    <>Be part of the plant-based coaching revolution.</>
                  </ListIcon>
                  <p>
                    <i>*With a special offer that’s only available for workshop participants!</i>
                  </p>
                </Col>
              </Row>
            </Container>
          </SectionCL>
        ) : null}

        <SectionCL id="pbccw-cta" color="black" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h5 className="section-heading">Join the Plant-Based Coaching Revolution</h5>
                <p className="balance-text">
                  Would you like to make a difference as a plant-based advocate? Or, are you looking{' '}
                  <br className="d-none d-xl-block" />
                  for a career (or a side gig) where you get paid to do deeply rewarding work?
                </p>
                <p className="balance-text mb-0">
                  Turn your love of plant-based foods into a thriving vocation <br className="d-none d-xl-block" />
                  that will allow you to help people <i>and</i> the planet.
                </p>
                <div>
                  <img
                    className="my-4"
                    src="https://cdn.foodrevolution.org/pbcc/pbcc-certificate-logo-white.svg"
                    alt="plant-based coaching certificate logo"
                    style={{maxHeight: '100px'}}
                  />
                </div>
                {!afterWorkshop && (
                  <>
                    <h5 className="text-400 mt-0 mb-2">
                      Apply before 11:59 pm Eastern on November 21st to be eligible for
                    </h5>
                    <p className="mb-4">
                      Special Tuition Discount (Save up to $1,585)
                      <br />
                      Unlimited Membership in WHOLE Life Club (Value $497/year)
                      <br />
                      Ocean Robbins’ Food Revolution Mastermind (Value $3,000)
                      <br />
                      2025-2026 Food Revolution Summit Empowerment Packages (Value $394)
                      <br />
                      And More!
                    </p>
                  </>
                )}
                <h3 className="mt-5 mb-3">Ready to join? Click the button below.</h3>
                <p>
                  <CheckoutButton
                    sku={productSku}
                    price={productValue}
                    slug={productSlug}
                    target="_blank"
                    disabled={mainPhase === 'after' ? true : false}
                  >
                    {mainPhase === 'after' ? <>Applications Are Closed</> : <>Apply Now</>}
                  </CheckoutButton>
                </p>
                <p className="text-small">
                  This takes you to a page where you can submit a $100 refundable deposit to hold your space. The
                  application takes about 10 minutes to complete. We’ll review your application, and if your application
                  is accepted, we’ll save your seat for two weeks so you can submit payment. Once you are officially
                  enrolled, your deposit counts towards your total tuition.
                </p>
                <h3 className="mt-5 mb-3">Have questions?</h3>
                <Button href="/join/" color="learn-more" target="_blank" size="lg">
                  Learn More
                </Button>
                {clarityCall && (
                  <>
                    <h3 className="mt-5 mb-3">Or speak with a program advisor.</h3>
                    <p>
                      <Button href="/calls/" color="learn-more" target="_blank" size="lg">
                        Book A Clarity Call
                      </Button>
                    </p>
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <CommentSection
          id="comments"
          numPosts="10"
          url={commentsUrl}
          isBroadcastPage
          shareUrl={shareUrl}
          shareImg={ogImage}
        />
      </div>

      <Modal isOpen={zoomInstructionsIsOpen} toggle={zoomInstructionsToggle} size="lg">
        <Container className="px-4 px-sm-5 pt-0 pb-5" fluid>
          <Row className="d-flex justify-content-center">
            <Col>
              <h5 className="section-heading text-center">How to join meetings and webinars with the web client:</h5>
              <p>Users can join Zoom meetings or webinars from their web browser and bypass downloading Zoom.</p>
              <ol>
                <li>Locate the webinar or meeting link</li>
                <li>Click the join link to join the meeting</li>
                <li>If a pop-up window prompts you to open or install the Zoom desktop client, click Cancel</li>
                <li>At the bottom of the page, click the Join from your Browser link</li>
                <li>Click Join</li>
              </ol>
              <p>Note: If you don't see this option, make sure to enable Join From Browser.</p>
              <p>
                Note: If prompted, sign in to your Zoom account. If you do not have a Zoom account, create a new account
                here{' '}
                <a href="https://zoom.us/" target="_blank">
                  https://zoom.us/
                </a>
                . You will be prompted to enter your name and the meeting password if it was not included in the join
                link.
              </p>
            </Col>
          </Row>
        </Container>
      </Modal>

      <div className="footer-content mt-auto">
        <Footer />
      </div>
      <script type="text/javascript" src="https://cdn.addevent.com/libs/atc/1.6.1/atc.min.js" async defer></script>
    </div>
  );
}
